import { useState } from 'react';

/**
 * A single consent gate. The declaration of individual work and the monitoring
 * disclaimer each get their own screen and their own checkbox — combining them
 * would make neither acceptance a meaningful record of what was agreed to.
 *
 * The text itself comes from the position config, so the wording reproduced in the
 * PDF is exactly the wording the candidate saw.
 */
export default function ConsentStep({
  kind,
  text,
  onAccept,
  busy,
}: {
  kind: 'declaration' | 'monitoring';
  text: string;
  onAccept: () => void;
  busy: boolean;
}) {
  const [checked, setChecked] = useState(false);

  const isDeclaration = kind === 'declaration';

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="text-2xl font-semibold">
        {isDeclaration ? 'Declaration of individual work' : 'Data and monitoring'}
      </h1>

      <p className="mt-2 text-sm text-ink/60">
        {isDeclaration
          ? 'This declaration and the time you accept it are reproduced in the report sent to the recruiting team.'
          : 'Please read this carefully. It describes exactly what is captured while you take the test.'}
      </p>

      <blockquote className="mt-6 rounded-md border-l-4 border-accent bg-surface p-5 text-sm leading-relaxed text-ink/90 shadow-sm">
        {text}
      </blockquote>

      {!isDeclaration ? (
        <ul className="mt-5 space-y-1.5 text-sm text-ink/70">
          <li>· Your IP address and approximate location, including any change during the test.</li>
          <li>· When the test window loses focus, and for how long.</li>
          <li>· Copy, cut and paste activity — the length of pasted text, never its contents.</li>
          <li>· Exits from fullscreen.</li>
          <li>· How long you spend on each question, and when you start typing.</li>
        </ul>
      ) : null}

      <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-md border border-ink/15 bg-surface p-4">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
        />
        <span className="text-sm font-medium">
          {isDeclaration
            ? 'I declare that I will complete this test entirely on my own, without help from any person, AI tool, search engine, or other assistance.'
            : 'I understand and accept how my connection and behavioural data is captured and used.'}
        </span>
      </label>

      <button
        type="button"
        className="btn-primary mt-6"
        disabled={!checked || busy}
        onClick={onAccept}
      >
        {busy ? 'Saving…' : 'Accept and continue'}
      </button>
    </div>
  );
}
